<template>
  <FlexLayout role="region">
    <vue-loading :active="isLoading" :is-full-page="true" />
  </FlexLayout>
</template>

<script>
import {Mode, Order, Workflow} from "@/constants/constants";
import {useLocaleStore} from "../../stores/locale";
import {useAppStore} from "../../stores/app";
import {useShopStore} from "@/stores/shop";
import orderMixin from "@/components/mixins/orderMixin";
import {useRegisterStore} from "@/stores/register";
import {useAuthStore} from "@/stores/auth";
import FlexLayout from "@/components/layout-components/Main/FlexLayout.vue";
import VueLoading from "vue-loading-overlay";
import {shopService} from "@/services/api/shopService";

export default {
  components: {
    FlexLayout,
    VueLoading
  },
  mixins: [orderMixin],
  data() {
    return {
      ordersPossible: true,
      isLoading: false,
    };
  },
  props: {
    shopCode: {
      Type: Number
    },
    tableCode: {
      Type: String
    },
    orderImmediate: {
      Type: Boolean
    },
    cardNumber: {
      Type: String
    },
    isMealPlan: {
      Type: Boolean
    },
    orderTypeId: {
      Type: Number
    },
    decideOnTable: {
      Type: String
    },
  },
  setup() {
    const localeStore = useLocaleStore();
    const appStore = useAppStore();
    const shopStore = useShopStore();
    const registerStore = useRegisterStore();
    const authStore = useAuthStore();

    return {
      localeStore,
      appStore,
      shopStore,
      registerStore,
      authStore,
    };
  },
  methods: {
    async changeLanguage(newLocale) {
      await this.localeStore.setLocale(newLocale);
    },
    async handleRegisterFlow(cardNumber) {
      this.appStore.setWorkflow(Workflow.DEFAULT);
      this.appStore.setMode(Mode.LOGIN);
      this.registerStore.setCardNumber(cardNumber);
      this.$navigateToExternalEntryRoute('Register', { withReplace: true });
    },
    async handleOrderAtShopAsGuestFlow(shopCode) {
      await this.startImmediateOrderFlow(shopCode, {
        orderType: Order.type.DINE_IN,
        numericOrderType: Order.numericType.DINE_IN,
        navigate: () => this.$navigateToExternalEntryRoute('OrderMenuPlanner', { withReplace: true }),
      });
    },
    async handleOrderMenuParametersPreselectFlow(shopCode) {
      this.appStore.setWorkflow(Workflow.ORDER);
      this.appStore.setMode(Mode.SHOP);
      const result = await shopService.selectShopByWondCode(shopCode, true);
      await this.doCheckIfOrdersAreCurrentlyPossible();
      this.$navigateToExternalEntryRoute('OrderMenuParameters', {
        params: { shopId: result.shop.id },
        withReplace: true,
      });
    },
    async handleOrderAtShopAndTableFlow(shopCode, tableCode) {
      this.appStore.setWorkflow(Workflow.ORDER);
      this.appStore.setMode(Mode.TABLE);
      this.appStore.setOrderEntryRoute('OrderMenuPlanner');
      await this.selectTableByQRCode(shopCode, tableCode, true);
      await this.doCheckIfOrdersAreCurrentlyPossible();
      this.appStore.setCurrentOrderMenuOrderType(Order.type.DINE_IN);
      this.$navigateToExternalEntryRoute('OrderMenuPlanner', { withReplace: true });
    },
    async handleOrderAtShopAndTableDecisionFlow(shopCode, tableCode) {
      this.appStore.setWorkflow(Workflow.ORDER);
      this.appStore.setMode(Mode.TABLE);

      // Bei nicht angemeldetem Benutzer erst Login erzwingen und danach Landing mit identischen Parametern erneut ausfuehren.
      if (!this.authStore.isLoggedIn) {
        this.appStore.setRedirectRoute(this.$route.name, this.$route.params, this.$route.query);
        this.$navigateToLogin();
        return;
      }

      // Flag setzen, damit Router-Guard die Navigation zu OrderMenuParameters im TABLE Mode erlaubt
      this.appStore.allowTableModeParametersNavigation();
      this.appStore.setOrderEntryRoute('OrderMenuParameters');

      const tableSelected = await this.selectTableByQRCode(shopCode, tableCode, true);
      if (!tableSelected) {
        this.appStore.disallowTableModeParametersNavigation();
        return;
      }

      await this.doCheckIfOrdersAreCurrentlyPossible();
      this.appStore.setCurrentOrderMenuOrderType(Order.type.DINE_IN);
      this.$navigateToExternalEntryRoute('TableOrder', { withReplace: true });
    },
    async handleTakeawayOrderFlow(shopCode) {
      this.appStore.setWorkflow(Workflow.ORDER);
      this.appStore.setMode(Mode.TAKEAWAY);

      // Bei nicht angemeldetem Benutzer erst Login erzwingen und danach Landing mit identischen Parametern erneut ausfuehren.
      if (!this.authStore.isLoggedIn) {
        this.appStore.setRedirectRoute(this.$route.name, this.$route.params, this.$route.query);
        this.$navigateToLogin();
        return;
      }

      // Flag setzen, damit Router-Guard die Navigation zu OrderMenuParameters im TAKEAWAY Mode erlaubt
      this.appStore.allowTableModeParametersNavigation();
      this.appStore.setOrderEntryRoute('OrderMenuParameters');

      const shopSelected = await shopService.selectShopByWondCode(shopCode, true);
      if (!shopSelected) {
        this.appStore.disallowTableModeParametersNavigation();
        return;
      }

      await this.doCheckIfOrdersAreCurrentlyPossible(Order.numericType.TAKEAWAY);
      this.appStore.setCurrentOrderMenuOrderType(Order.type.TAKEAWAY);
      this.$navigateToExternalEntryRoute('TakeawayOrder', { withReplace: true });
    },
  },
  created() {
    // Register flow (prefilled card number)
    if (this.cardNumber) {
      this.handleRegisterFlow(this.cardNumber);
      return;
    }

    if (this.shopCode) {
      // Table order flow
      if (this.tableCode) {
        if (this.decideOnTable) {
          console.log("Table order flow with decision", this.shopCode, this.tableCode);
          this.handleOrderAtShopAndTableDecisionFlow(this.shopCode, this.tableCode);
        } else {
          console.log("Table order flow", this.shopCode, this.tableCode);
          this.handleOrderAtShopAndTableFlow(this.shopCode, this.tableCode);
        }

      } else if (this.orderTypeId === Order.numericType.TAKEAWAY) {
        console.log("Takeaway order flow", this.shopCode, this.orderTypeId);
        this.handleTakeawayOrderFlow(this.shopCode);
      } else if (this.orderImmediate) {
        console.log("Immediate order flow", this.shopCode, this.orderImmediate);
        this.handleOrderAtShopAsGuestFlow(this.shopCode, this.orderImmediate);
      } else {
        console.log("Order menu parameters preselect flow", this.shopCode);
        this.handleOrderMenuParametersPreselectFlow(this.shopCode);
      }
      return;
    }

    this.$navigateToErrorLanding();
  }
};
</script>
